// ─── Hook : FORMULAIRE d'événement (état + validation + actions) ──
// Extrait d'EcranCalendrier (découpage). Porte l'état du formulaire (form,
// picker, erreurs, busy), les ouvertures (ajout, créneau, édition, série),
// la validation app-wide (champs rouges + disquette 3 états), l'enregistrement
// (simple / série / TRANSFORMATION simple→récurrent) et les actions liées
// (supprimer, rétablir une exception, déplacer une occurrence, gérer la série).
import { useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import {
  useUI, useT, useAuth, useModifie, styleChampErreur, confirmerSuppression, confirmerChoix, journaliser,
} from '@mop/core';
import {
  ajouterEvenement, modifierEvenement, supprimerEvenement,
  ajouterEvenementRecurrent, supprimerEvenementRecurrent, modifierEvenementRecurrent,
  retirerExceptionRecurrent, ajouterExceptionRecurrent, lireEvenementRecurrent,
  deplacerOccurrenceRecurrente, majDeplacementRecurrent, nettoyerDeplacementRecurrent,
} from '../services/evenements';

const fmtJour = (dk) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dk || '')) return '—'; const [y, m, d] = dk.split('-'); return `${d}/${m}/${y}`; };
const dateOk = (s) => /^\d{4}-\d{2}-\d{2}$/.test((s || '').trim());

export default function useFormulaireEvenement({ calId, edit, cfg, dkSel, allerJour, apresEnregistrer, onMajFiche }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const [form, setForm] = useState(null);   // { id?, recId?, dk, titre, heure, note, ... }
  // Disquette grisée tant que rien n'a changé (hook Core useModifie).
  const formCmp = (f) => f ? { titre: (f.titre || '').trim(), categorie: f.categorie || '', dk: f.dk || '', dateFinEvt: (f.dateFinEvt || '').trim(), jourEntier: !!f.jourEntier, heure: f.heure || '', heureFin: f.heureFin || '', note: (f.note || '').trim(), pieces: (f.pieces || []).map(p => p?.url || p), repet: f.repet || '', intervalle: Number(f.intervalle) || 1, joursSemaine: (f.joursSemaine || []).slice().sort().join(','), lieuId: f.lieuId || '' } : null;
  const { modifie: formChange, capturer: capturerForm } = useModifie(formCmp(form));
  const ouvrirForm = (f) => { capturerForm(formCmp(f)); setForm(f); };
  const [picker, setPicker] = useState(null);   // { type:'date'|'heure', champ, titre, avecEffacer }
  const ouvrirPicker = (p) => { Keyboard.dismiss(); setPicker(p); };   // réduit le clavier avant d'ouvrir un sélecteur
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [catForm, setCatForm] = useState(false);          // accordéon « catégorie »
  const [deplacerOcc, setDeplacerOcc] = useState(null);   // { ev, dk, cal, mode:'rec'|'simple' }

  // ── Ouvertures ──
  const vierge = (dk) => ({ dk, dkInitial: dk, calEvt: calId, titre: '', categorie: '', jourEntier: false, heure: '', heureFin: '', dateFinEvt: '', note: '', pieces: [], repet: '', intervalle: 1, joursSemaine: [], dateFin: '' });
  const ouvrirAjout = () => { setFormErr(''); setCatForm(false); ouvrirForm(vierge(dkSel)); };
  // Minutes ↔ "HH:MM" (borné même jour) + durée par défaut de la config.
  const HHMM = (m) => { const x = Math.max(0, Math.min(1439, Math.round(m))); return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`; };
  const minDe = (hhmm) => { const [h, mi] = String(hhmm || '').split(':').map(Number); return (h || 0) * 60 + (mi || 0); };
  const finParDefaut = (heure) => HHMM(minDe(heure) + (cfg.dureeDefaut || 60));
  // Appui long sur un créneau (vues Semaine/Jour) → ajout pré-rempli : jour cliqué,
  // heure de début = créneau (arrondi 15 min), heure de fin = début + durée par défaut.
  const dkDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const ouvrirAjoutCreneau = (d, minRaw) => {
    const m = Math.max(0, Math.min(1425, Math.round(minRaw / 15) * 15));
    const dk = dkDe(d);
    allerJour(d);
    setFormErr(''); setCatForm(false);
    ouvrirForm({ ...vierge(dk), heure: HHMM(m), heureFin: finParDefaut(HHMM(m)) });
  };
  const ouvrirEdition = (ev, dk = dkSel) => {
    setFormErr(''); setCatForm(false);
    // Une PLAGE affichée est une occurrence étalée : on édite l'événement d'ORIGINE
    // (plageId + jour/heures source), pas l'occurrence (id « plage_… », heure vidée).
    const estP = !!ev.estPlage;
    const id = estP ? ev.plageId : ev.id;
    const src = estP ? (ev.plageSourceDk || dk || dkSel) : (dk || dkSel);
    const heure = estP ? (ev.plageHeureDebut || '') : (ev.heure || '');
    const heureFin = estP ? (ev.plageHeureFin || '') : (ev.heureFin || '');
    ouvrirForm({ id, dk: src, dkInitial: src, calEvt: ev._calId || calId, titre: ev.titre || '', categorie: ev.categorie || '', jourEntier: !heure, heure, heureFin, dateFinEvt: ev.dateFin || '', note: ev.note || '', pieces: ev.pieces || [], lieu: ev.lieu || '', lieuId: ev.lieuId || '', repet: '', intervalle: 1, joursSemaine: [], dateFin: '' });
  };
  // Édite la SÉRIE d'un événement récurrent (l'occurrence porte recDef).
  function ouvrirEditionRecurrence(ev) {
    setFormErr(''); setCatForm(false);
    const rec = ev.recDef || {};
    ouvrirForm({
      recId: ev.recId, dk: rec.dateDebut || dkSel, dkInitial: rec.dateDebut || dkSel,
      calEvt: ev._calId || calId,
      titre: ev.titre || '', categorie: ev.categorie || '',
      jourEntier: !ev.heure, heure: ev.heure || '', heureFin: ev.heureFin || '',
      dateFinEvt: rec.dateFin || '', note: ev.note || '', pieces: ev.pieces || [], lieu: ev.lieu || '', lieuId: ev.lieuId || '',
      repet: rec.type === 'bimensuel' ? 'hebdo' : (rec.type || 'quotidien'),
      intervalle: rec.type === 'bimensuel' ? 2 : (Number(rec.intervalle) || 1),
      joursSemaine: rec.joursSemaine || [], dateFin: '',
    });
  }

  // ── Validation (principe app-wide : champs rouges + disquette 3 états) ──
  // En RÉCURRENCE, la date de fin est la fin de récurrence : chaque occurrence
  // reste MONO-JOUR → heures toujours comparées.
  const monoJour = !!form && (!!form.repet || !!form.recId || !(form?.dateFinEvt || '').trim() || form?.dateFinEvt === form?.dk);
  // Un événement HORAIRE (non « jour entier ») doit TOUJOURS avoir une heure de fin.
  const heureFinManquante = !!form && !form.jourEntier && !!(form.heure || '').trim() && !(form.heureFin || '').trim();
  const errHeures = !!form && monoJour && !form.jourEntier
    && !!(form.heure || '').trim() && !!(form.heureFin || '').trim() && form.heureFin.trim() < form.heure.trim();
  const errDates = !!form && !!(form.dateFinEvt || '').trim() && dateOk(form.dateFinEvt) && dateOk(form.dk)
    && form.dateFinEvt.trim() < form.dk.trim();
  const formValide = !!form && !!form.titre.trim() && dateOk(form.dk)
    && (!(form.dateFinEvt || '').trim() || (dateOk(form.dateFinEvt) && !errDates))
    && !heureFinManquante && !errHeures;
  // Disquette « grisée + pastille ! » : modification présente mais erreur bloquante.
  const formEnErreur = formChange && (errHeures || errDates || heureFinManquante);
  const CE = styleChampErreur(UI);
  const scrollForm = useRef(null);
  const posErr = useRef({});
  const [flashErr, setFlashErr] = useState(0);
  const clignoter = () => { let n = 6; const it = setInterval(() => { n -= 1; if (n <= 0) { clearInterval(it); setFlashErr(0); } else setFlashErr(x => x + 1); }, 170); };
  const montrerErreurs = () => {
    const y = errDates ? posErr.current.dates : posErr.current.heures;
    if (y != null) scrollForm.current?.scrollTo({ y: Math.max(0, y - 14), animated: true });
    clignoter();
  };
  const estompe = (on) => (on && flashErr % 2 === 1) ? { opacity: 0.3 } : null;
  // Disquette active seulement si VALIDE **et** modifié depuis l'ouverture (app-wide).
  const formModifie = formValide && formChange;
  const toggleJour = (i) => setForm(x => ({ ...x, joursSemaine: x.joursSemaine.includes(i) ? x.joursSemaine.filter(j => j !== i) : [...x.joursSemaine, i] }));

  // ── Enregistrement ──
  async function enregistrer() {
    if (!form || !formModifie || busy) return;
    if (!calId) { setFormErr(t('calendrier.erreurAucunCalendrier')); return; }
    setBusy(true); setFormErr('');
    try {
      const dk = form.dk.trim();
      const finEvt = (form.dateFinEvt || '').trim();
      const dateFin = (finEvt && finEvt > dk) ? finEvt : null;   // événement multi-jour
      // `type` STOCKÉ (comme MoPCal) : 'simple' à la création manuelle ; les
      // séries récurrentes portent 'recurrent' ; 'repas'/'edt' viennent des modules.
      const champs = { titre: form.titre.trim(), categorie: form.categorie || null, type: form.repet ? 'recurrent' : 'simple', heure: form.jourEntier ? null : (form.heure.trim() || null), heureFin: form.jourEntier ? null : (form.heureFin.trim() || null), note: form.note.trim() || null, dateFin, pieces: form.pieces || [], lieu: (form.lieu || '').trim() || null, lieuId: form.lieuId || null };
      const base = { creePar: user?.uid, creeParNom: user?.prenom || user?.email || '' };
      const auteur = { uid: user?.uid, nom: user?.prenom || user?.email || '' };
      const calEvt = form.calEvt || calId;   // édition → calendrier de l'événement ; création → calendrier actif
      if (form.id && !form.repet) {
        if (dk !== form.dkInitial) {
          // La date de début a changé → déplacer l'événement (supprime + recrée,
          // id conservé). Les DEUX écritures restent silencieuses (sansJournal) :
          // une seule entrée « modification » les résume, sinon le déplacement
          // d'un simple tap laisserait DEUX lignes dans l'historique pour un
          // seul geste (même patron que MoPCal, `deplacerEvenement`).
          await supprimerEvenement(calEvt, form.dkInitial, form.id, auteur, { sansJournal: true });
          await ajouterEvenement(calEvt, dk, { ...champs, id: form.id, ...base }, { sansJournal: true });
          journaliser(calEvt, {
            type: 'evenement', action: 'modification', titre: champs.titre,
            dateConcernee: dk, detail: { champ: 'date', ancien: form.dkInitial, nouveau: dk },
            par: auteur.uid || null, parNom: auteur.nom || '',
          });
        } else {
          await modifierEvenement(calEvt, dk, form.id, champs, auteur);
        }
      } else if (form.recId || form.repet) {
        // Série récurrente (édition ou création). La date de fin de l'événement
        // sert de fin de RÉCURRENCE. intervalle k : toutes les k semaines
        // (k=2 → type `bimensuel`, compat MoPCal) / tous les k mois.
        const [y, m, d] = dk.split('-').map(Number);
        const wd = new Date(y, m - 1, d).getDay();
        const k = Math.max(1, Math.min(52, parseInt(form.intervalle, 10) || 1));
        const recurrence = { dateDebut: dk, ...(finEvt ? { dateFin: finEvt } : {}) };
        if (form.repet === 'hebdo') {
          recurrence.type = k === 2 ? 'bimensuel' : 'hebdo';
          if (k > 2) recurrence.intervalle = k;
          recurrence.joursSemaine = form.joursSemaine.length ? form.joursSemaine : [wd];
        } else if (form.repet === 'mensuel') {
          recurrence.type = 'mensuel';
          if (k > 1) recurrence.intervalle = k;
          if (form.joursSemaine.length) recurrence.joursSemaine = form.joursSemaine;
        } else {
          recurrence.type = form.repet || 'quotidien';
          if (recurrence.type === 'quotidien' && k > 1) recurrence.intervalle = k;   // chaque k jours
        }
        if (form.recId) {
          // Occurrences DÉPLACÉES qui sortent de la nouvelle période → popup :
          // l'utilisateur choisit de garder ou supprimer ces événements (liste
          // des seules exceptions problématiques).
          let problematiques = [], depsRef = {};
          try {
            const recDoc = await lireEvenementRecurrent(calEvt, form.recId);
            depsRef = recDoc?.deplacements || {};
            const dansPlage = (x) => x >= dk && (!finEvt || x <= finEvt);
            problematiques = Object.keys(depsRef).filter(x => !dansPlage(x));
          } catch (_) {}
          await modifierEvenementRecurrent(calEvt, form.recId, { ...champs, dateFin: null, recurrence }, { ...auteur, titre: champs.titre });
          if (problematiques.length) {
            const recIdCourant = form.recId;
            const sep = String.fromCharCode(10);
            const liste = problematiques.sort().map(x => `⇄ ${fmtJour(x)} → ${fmtJour(depsRef[x].dk)}`).join(sep);
            confirmerChoix(t('calendrier.evtsDeplacesTitre'), `${t('calendrier.evtsDeplacesMsg')}${sep}${sep}${liste}`, [
              { text: t('calendrier.garderEvts'), style: 'cancel' },
              { text: t('calendrier.supprimerEvts'), style: 'destructive',
                onPress: () => problematiques.forEach(x => retirerExceptionRecurrent(calEvt, recIdCourant, x).catch(() => {})) },
            ]);
          }
        }
        else {
          await ajouterEvenementRecurrent(calEvt, { ...champs, dateFin: null, ...base, recurrence, exceptions: [] });
          // TRANSFORMATION d'un événement simple existant : la série le
          // remplace, l'événement daté d'origine est retiré.
          if (form.id) await supprimerEvenement(calEvt, form.dkInitial, form.id, auteur).catch(() => {});
        }
      } else {
        await ajouterEvenement(calId, dk, { ...champs, ...base });
      }
      setForm(null);
      apresEnregistrer?.();
    } catch (e) { setFormErr(e?.message || String(e) || t('calendrier.erreurEnregistrement')); }
    setBusy(false);
  }

  // ── Actions liées ──
  function supprimer(ev) {
    const cal = ev._calId || calId;
    // Une PLAGE affichée est une occurrence ÉTALÉE (id « plage_… » sur chaque
    // jour traversé) : on supprime l'événement d'ORIGINE, à son jour source —
    // sinon la suppression ne trouve rien et la plage semble indestructible.
    const estP = !!ev.estPlage;
    const id = estP ? ev.plageId : ev.id;
    const dk = estP ? (ev.plageSourceDk || dkSel) : dkSel;
    confirmerSuppression(t('evenements.supprimerEvenement'), t('calendrier.supprEvtMsg', { titre: ev.titre }),
      async () => {
        try {
          await supprimerEvenement(cal, dk, id, { uid: user?.uid, nom: user?.prenom || user?.email || '' });
          if (ev.recIdOrigine && ev.deplaceDe) await nettoyerDeplacementRecurrent(cal, ev.recIdOrigine, ev.deplaceDe).catch(() => {});
        } catch (_) {}
      });
  }
  // Rétablit un jour ignoré d'une série (supprime l'exception) + met à jour la
  // fiche ouverte (instantané local, via onMajFiche = setEvtInfo du parent).
  function retablirException(ev, dkExc) {
    retirerExceptionRecurrent(ev._calId || calId, ev.recId, dkExc).catch(() => {});
    onMajFiche?.(prev => {
      if (!prev) return prev;
      const dep = { ...(prev.ev.deplacements || {}) }; delete dep[dkExc];
      return { ...prev, ev: { ...prev.ev, recExceptions: (prev.ev.recExceptions || []).filter(x => x !== dkExc), deplacements: dep } };
    });
  }
  // DÉPLACER une occurrence : exception sur le jour d'origine (réversible) +
  // copie SIMPLE au jour choisi (liée à la série si mode 'rec').
  async function deplacerOccurrence(dkNouv) {
    const d = deplacerOcc; setDeplacerOcc(null);
    if (!d || !dkNouv || dkNouv === d.dk) return;
    const ev = d.ev;
    try {
      if (d.mode === 'simple') {
        // Événement SIMPLE mono-jour : pur changement de date (id conservé).
        // sansJournal + une seule entrée « modification » — même patron que le
        // déplacement par le formulaire (cf. enregistrer()).
        const { _calId, _couleur, isRecurrent, estDeplace, recDef, recExceptions, deplacements, recId, ...reste } = ev;
        await supprimerEvenement(d.cal, d.dk, ev.id, null, { sansJournal: true });
        await ajouterEvenement(d.cal, dkNouv, reste, { sansJournal: true });
        journaliser(d.cal, {
          type: 'evenement', action: 'modification', titre: ev.titre || '',
          dateConcernee: dkNouv, detail: { champ: 'date', ancien: d.dk, nouveau: dkNouv },
          par: user?.uid || null, parNom: user?.prenom || user?.email || '',
        });
        // Copie issue d'un déplacement de série : la trace suit le nouveau jour.
        if (ev.recIdOrigine && ev.deplaceDe) await majDeplacementRecurrent(d.cal, ev.recIdOrigine, ev.deplaceDe, dkNouv, ev.id).catch(() => {});
      } else {
        // Occurrence de SÉRIE : exception + copie LIÉE (voir la fiche de la série).
        await deplacerOccurrenceRecurrente(d.cal, ev.recId, d.dk, dkNouv, {
          titre: ev.titre || '', categorie: ev.categorie || null,
          heure: ev.heure || null, heureFin: ev.heureFin || null,
          note: ev.note || null, dateFin: null, pieces: ev.pieces || [], lieu: ev.lieu || null, lieuId: ev.lieuId || null,
          creePar: user?.uid, creeParNom: user?.prenom || user?.email || '',
        });
      }
    } catch (_) {}
  }
  // Suppression de toute la SÉRIE : toujours confirmée par popup.
  function supprimerSerie(ev) {
    confirmerSuppression(t('calendrier.supprimerTouteSerie'), t('calendrier.evtTitreGuillemets', { titre: ev.titre }),
      () => supprimerEvenementRecurrent(ev._calId || calId, ev.recId,
        { uid: user?.uid, nom: user?.prenom || user?.email || '', titre: ev.titre || '' }).catch(() => {}));
  }
  // Tap sur une occurrence récurrente → gérer la série (sur son calendrier).
  function gererRecurrence(ev, dk = dkSel) {
    if (!edit) return;
    const cal = ev._calId || calId;
    confirmerChoix(`${t('evenements.evenementRecurrent')} 🔁`, t('calendrier.evtTitreGuillemets', { titre: ev.titre }), [
      { text: t('calendrier.ignorerCeJour'), onPress: () => ajouterExceptionRecurrent(cal, ev.recId, dk || dkSel).catch(() => {}) },
      { text: t('calendrier.supprimerTouteSerie'), style: 'destructive', onPress: () => supprimerSerie(ev) },
      { text: t('commun.annuler'), style: 'cancel' },
    ]);
  }

  return {
    form, setForm, picker, setPicker, ouvrirPicker, formErr, setFormErr, busy,
    catForm, setCatForm, errDates, errHeures, heureFinManquante, formModifie, formEnErreur,
    CE, scrollForm, posErr, montrerErreurs, estompe, toggleJour, finParDefaut,
    ouvrirAjout, ouvrirAjoutCreneau, ouvrirEdition, ouvrirEditionRecurrence,
    enregistrer, supprimer, retablirException,
    deplacerOcc, setDeplacerOcc, deplacerOccurrence, supprimerSerie, gererRecurrence,
  };
}
